import { useRef, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Stack,
  Box, Alert, Table, TableBody, TableCell, TableHead, TableRow, Chip, alpha, useTheme,
  LinearProgress,
} from '@mui/material';
import { UploadFile, Download, CloudUpload } from '@mui/icons-material';
import type { MasterSchema } from '../../constants/masters';
import { downloadMasterTemplate, parseMasterCsv } from '../../utils/masterCsv';
import type { MasterCsvRow } from '../../utils/masterCsv';

interface MasterImportDialogProps {
  open: boolean;
  onClose: () => void;
  masterType: string;
  masterLabel: string;
  schema?: MasterSchema | null;
  loading?: boolean;
  onImport: (rows: MasterCsvRow[]) => void;
  result?: { created: number; updated: number; failed: { code: string; error: string }[] } | null;
}

export default function MasterImportDialog({
  open,
  onClose,
  masterType,
  masterLabel,
  schema,
  loading,
  onImport,
  result,
}: MasterImportDialogProps) {
  const theme = useTheme();
  const fileRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<MasterCsvRow[]>([]);
  const [parseError, setParseError] = useState('');

  const handleFile = (file: File) => {
    setParseError('');
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const rows = parseMasterCsv(String(reader.result || ''), schema);
        if (!rows.length) {
          setParseError('No valid rows found. Check CSV headers match the template.');
          setPreview([]);
          return;
        }
        setPreview(rows);
      } catch {
        setParseError('Could not parse CSV file.');
        setPreview([]);
      }
    };
    reader.readAsText(file);
  };

  const handleClose = () => {
    setPreview([]);
    setParseError('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" fontWeight={800}>Bulk Import — {masterLabel}</Typography>
        <Typography variant="body2" color="text.secondary">
          Upload a CSV file. Existing codes will be updated; new codes will be created.
        </Typography>
      </DialogTitle>
      <DialogContent>
        {loading && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2 }}>
          <Button
            variant="outlined"
            startIcon={<Download />}
            onClick={() => downloadMasterTemplate(masterType, schema)}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Download Template
          </Button>
          <Button
            variant="contained"
            startIcon={<CloudUpload />}
            onClick={() => fileRef.current?.click()}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            Choose CSV File
          </Button>
          <input
            ref={fileRef}
            type="file"
            accept=".csv,text/csv"
            hidden
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleFile(file);
              e.target.value = '';
            }}
          />
        </Stack>

        {parseError && <Alert severity="error" sx={{ mb: 2 }}>{parseError}</Alert>}

        {result && (
          <Alert severity={result.failed.length ? 'warning' : 'success'} sx={{ mb: 2 }}>
            Imported: {result.created} created, {result.updated} updated
            {result.failed.length > 0 && `, ${result.failed.length} failed`}
          </Alert>
        )}

        {preview.length > 0 && (
          <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
            <Box sx={{ px: 2, py: 1, bgcolor: alpha(theme.palette.primary.main, 0.06), borderBottom: 1, borderColor: 'divider' }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <UploadFile fontSize="small" color="primary" />
                <Typography variant="subtitle2" fontWeight={700}>
                  Preview ({preview.length} rows)
                </Typography>
              </Stack>
            </Box>
            <Box sx={{ maxHeight: 280, overflow: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>S.No</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Code</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {preview.slice(0, 50).map((row, i) => (
                    <TableRow key={`${row.code}-${i}`}>
                      <TableCell>{i + 1}</TableCell>
                      <TableCell><Typography variant="caption" fontFamily="monospace">{row.code}</Typography></TableCell>
                      <TableCell>{row.name}</TableCell>
                      <TableCell>
                        <Chip size="small" label={row.isActive !== false ? 'Active' : 'Off'} variant="outlined" />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
            {preview.length > 50 && (
              <Typography variant="caption" color="text.secondary" sx={{ p: 1.5, display: 'block' }}>
                Showing first 50 of {preview.length} rows
              </Typography>
            )}
          </Box>
        )}

        {!preview.length && !parseError && (
          <Box
            sx={{
              py: 4,
              textAlign: 'center',
              border: 2,
              borderStyle: 'dashed',
              borderColor: 'divider',
              borderRadius: 2,
              bgcolor: alpha(theme.palette.primary.main, 0.02),
            }}
          >
            <CloudUpload sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              Download the template, fill in your data, then upload the CSV
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!preview.length || loading}
          onClick={() => onImport(preview)}
          sx={{ textTransform: 'none', fontWeight: 700 }}
        >
          Import {preview.length ? `${preview.length} Records` : ''}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
